/**
 * Unified Theme Configuration for AUMS
 * This file defines the consistent design system used throughout the application
 */

export const theme = {
  // Color Palette
  colors: {
    // Primary Colors - Blue gradient theme
    primary: '#1890ff',
    primaryLight: '#40a9ff',
    primaryDark: '#096dd9',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    primaryGradientAlt: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    
    // Status Colors
    success: '#52c41a',
    successLight: '#73d13d',
    warning: '#faad14',
    warningLight: '#ffc53d',
    error: '#ff4d4f',
    errorLight: '#ff7875',
    info: '#1890ff',
    
    // Neutral Colors
    background: '#f5f5f5',
    backgroundGradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    surface: '#ffffff',
    surfaceHover: '#fafafa',
    border: '#e8e8e8',
    borderLight: '#f0f0f0',
    text: '#262626',
    textSecondary: '#595959',
    textTertiary: '#8c8c8c',
    textDisabled: '#bfbfbf',
    
    // Header/Sidebar
    headerBg: '#001529',
    sidebarBg: '#001529',
    sidebarHover: '#000c17',
    
    // Card Colors
    cardBg: '#ffffff',
    cardHover: '#f0f5ff',
    cardBorder: '#e8e8e8',
    
    // Input Colors
    inputBg: '#ffffff',
    inputBgDisabled: '#f5f5f5',
    inputBorder: '#d9d9d9',
    inputBorderFocus: '#1890ff',
    inputShadow: '0 0 0 2px rgba(24, 144, 255, 0.1)',
  },
  
  // Typography
  typography: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontSize: {
      xs: '11px',
      sm: '12px',
      base: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  
  // Spacing
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '28px',
    '4xl': '32px',
    '5xl': '40px',
  },
  
  // Border Radius
  borderRadius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 2px 8px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.08)',
    lg: '0 4px 20px rgba(0,0,0,0.08)',
    xl: '0 8px 24px rgba(0,0,0,0.12)',
    primary: '0 2px 8px rgba(24, 144, 255, 0.3)',
    primaryHover: '0 4px 12px rgba(24, 144, 255, 0.4)',
    success: '0 2px 8px rgba(82, 196, 26, 0.3)',
  },
  
  // Transitions
  transitions: {
    fast: '0.15s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
    smooth: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  // Z-Index
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
}

// Ant Design Theme Configuration
export const antdTheme = {
  token: {
    colorPrimary: theme.colors.primary,
    colorSuccess: theme.colors.success,
    colorWarning: theme.colors.warning,
    colorError: theme.colors.error,
    colorInfo: theme.colors.info,
    borderRadius: parseInt(theme.borderRadius.md),
    wireframe: false,
    fontFamily: theme.typography.fontFamily,
  },
  components: {
    Layout: {
      headerBg: theme.colors.headerBg,
      siderBg: theme.colors.sidebarBg,
    },
    Menu: {
      darkItemBg: theme.colors.sidebarBg,
      darkSubMenuItemBg: theme.colors.sidebarHover,
      itemBorderRadius: parseInt(theme.borderRadius.sm),
    },
    Button: {
      borderRadius: parseInt(theme.borderRadius.md),
      primaryShadow: theme.shadows.primary,
    },
    Card: {
      borderRadius: parseInt(theme.borderRadius.xl),
      paddingLG: theme.spacing['2xl'],
    },
    Input: {
      borderRadius: parseInt(theme.borderRadius.md),
      activeBorderColor: theme.colors.inputBorderFocus,
      hoverBorderColor: theme.colors.primaryLight,
    },
    Table: {
      borderRadius: parseInt(theme.borderRadius.xl),
    },
    Modal: {
      borderRadius: parseInt(theme.borderRadius.xl),
    },
    Tag: {
      borderRadius: parseInt(theme.borderRadius.sm),
    },
  },
}

// Common Style Objects
export const commonStyles = {
  // Page Container
  pageContainer: {
    padding: theme.spacing['2xl'],
    background: theme.colors.backgroundGradient,
    minHeight: 'calc(100vh - 144px)',
  },
  
  // Header Card
  headerCard: {
    marginBottom: theme.spacing.xl,
    borderRadius: theme.borderRadius.xl,
    border: 'none',
    boxShadow: theme.shadows.lg,
    background: theme.colors.primaryGradient,
    color: '#fff',
  },
  
  // Content Card
  contentCard: {
    borderRadius: theme.borderRadius.xl,
    border: 'none',
    boxShadow: theme.shadows.lg,
    overflow: 'hidden',
  },
  
  // Primary Button
  primaryButton: {
    borderRadius: theme.borderRadius.md,
    height: '40px',
    boxShadow: theme.shadows.primary,
    border: 'none',
  },
  
  // Secondary Button
  secondaryButton: {
    borderRadius: theme.borderRadius.md,
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff',
  },
  
  // Input
  input: {
    borderRadius: theme.borderRadius.md,
    background: 'rgba(255,255,255,0.95)',
  },
  
  // Modal
  modal: {
    borderRadius: theme.borderRadius.xl,
    top: 50,
  },
  
  // Modal Buttons
  modalButton: {
    borderRadius: theme.borderRadius.md,
    height: '40px',
    padding: '0 24px',
  },
}

export default theme

